import { Capacitor } from '@capacitor/core';

const PERMISSION_FLAG = 'skipdq_notif_permission_requested';
const LEGACY_PERMISSION_FLAG = 'trucktime_notif_permission_requested';
export const NEW_ORDERS_CHANNEL = 'new_orders';
export const ORDER_UPDATES_CHANNEL = 'order_updates';

/** Web path for the shared alert sound (also copied into native projects). */
export const NEW_ORDER_SOUND_WEB = '/sounds/new-order.wav';

/** iOS LocalNotifications sound filename (must be in the app bundle). */
export const NEW_ORDER_SOUND_IOS = 'new-order.wav';

/** Android res/raw filename (with extension) for channel sound. */
export const NEW_ORDER_SOUND_ANDROID = 'new_order.wav';

export const NOTIF_KIND_NEW = 'new';
export const NOTIF_KIND_UNAPPROVED = 'unapproved30';
export const NOTIF_KIND_NOT_READY = 'notready15';

const ORDER_NOTIF_KINDS = [NOTIF_KIND_NEW, NOTIF_KIND_UNAPPROVED, NOTIF_KIND_NOT_READY];

function isNative() {
  return Capacitor.isNativePlatform();
}

async function getLocalNotifications() {
  // Lazy import so web builds never pull native code paths at module load.
  const mod = await import('@capacitor/local-notifications');
  return mod.LocalNotifications;
}

let channelsReady = false;

async function ensureAndroidChannels(LocalNotifications) {
  if (channelsReady || Capacitor.getPlatform() !== 'android') return;
  try {
    await LocalNotifications.createChannel({
      id: NEW_ORDERS_CHANNEL,
      name: 'New orders',
      description: 'Alerts when a customer places an order',
      importance: 5,
      sound: NEW_ORDER_SOUND_ANDROID,
      vibration: true,
      visibility: 1,
    });
    await LocalNotifications.createChannel({
      id: ORDER_UPDATES_CHANNEL,
      name: 'Order updates',
      description: 'Status updates for your orders',
      importance: 4,
      sound: NEW_ORDER_SOUND_ANDROID,
      vibration: true,
      visibility: 1,
    });
    channelsReady = true;
  } catch (error) {
    console.warn('createChannel failed:', error);
  }
}

/** Stable positive int id so re-fires replace instead of stacking. */
export function notificationIdFor(orderId, kind) {
  const s = `${kind || 'x'}:${orderId || ''}`;
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) % 2147483646 + 1;
}

let alertAudio = null;
let alertPlayWarned = false;

/** Play the alert tone in-app (web, or native foreground fallback). */
export function playAlertSound() {
  try {
    if (!alertAudio) {
      alertAudio = new Audio(NEW_ORDER_SOUND_WEB);
      alertAudio.preload = 'auto';
    }
    alertAudio.volume = 1;
    try {
      alertAudio.pause();
      alertAudio.currentTime = 0;
    } catch (_) {
      /* ignore seek errors on fresh instances */
    }
    const p = alertAudio.play();
    if (p && typeof p.catch === 'function') {
      p.catch((err) => {
        if (!alertPlayWarned) {
          alertPlayWarned = true;
          console.warn('playAlertSound failed:', err?.message || err);
        }
      });
    }
  } catch (e) {
    if (!alertPlayWarned) {
      alertPlayWarned = true;
      console.warn('playAlertSound failed:', e?.message || e);
    }
  }
}

// Ask for notification permission at most once (native or web).
export async function requestNotificationPermission() {
  try {
    if (isNative()) {
      const LocalNotifications = await getLocalNotifications();
      await ensureAndroidChannels(LocalNotifications);
      const result = await LocalNotifications.requestPermissions();
      return result?.display === 'granted';
    }
    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'granted') return true;
      if (Notification.permission === 'denied') return false;
      const perm = await Notification.requestPermission();
      return perm === 'granted';
    }
  } catch (error) {
    console.warn('Notification permission request failed:', error);
  }
  return false;
}

// Request permission only once per device, ideally after phone verification.
export async function ensureNotificationPermissionOnce() {
  try {
    if (localStorage.getItem(PERMISSION_FLAG) || localStorage.getItem(LEGACY_PERMISSION_FLAG)) {
      if (!localStorage.getItem(PERMISSION_FLAG) && localStorage.getItem(LEGACY_PERMISSION_FLAG)) {
        localStorage.setItem(PERMISSION_FLAG, '1');
        localStorage.removeItem(LEGACY_PERMISSION_FLAG);
      }
      return;
    }
    await requestNotificationPermission();
    // Only remember once the user actually answered, so a dismissed/failed prompt is retried.
    if (await permissionDecided()) localStorage.setItem(PERMISSION_FLAG, '1');
  } catch (error) {
    console.warn('ensureNotificationPermissionOnce failed:', error);
  }
}

async function permissionDecided() {
  if (isNative()) {
    const LocalNotifications = await getLocalNotifications();
    const perm = await LocalNotifications.checkPermissions();
    return perm?.display === 'granted' || perm?.display === 'denied';
  }
  if (typeof window !== 'undefined' && 'Notification' in window) {
    return Notification.permission !== 'default';
  }
  return true;
}

/**
 * Fire an instant notification (native local notification or web Notification).
 * Stays in Notification Center until the user clears it (or we cancel by id).
 * @param {{ title: string, body?: string, sound?: boolean, channelId?: string, playInAppSound?: boolean, id?: number, orderId?: string, kind?: string }} opts
 */
export async function notify({
  title,
  body,
  sound = true,
  channelId,
  playInAppSound = false,
  id,
  orderId,
  kind,
} = {}) {
  try {
    if (sound && playInAppSound) {
      playAlertSound();
    }

    const notifId =
      typeof id === 'number'
        ? id
        : orderId
          ? notificationIdFor(orderId, kind || 'generic')
          : Math.floor(Math.random() * 2147483647);

    if (isNative()) {
      const LocalNotifications = await getLocalNotifications();
      await ensureAndroidChannels(LocalNotifications);
      const perm = await LocalNotifications.checkPermissions();
      if (perm?.display !== 'granted') {
        const req = await LocalNotifications.requestPermissions();
        if (req?.display !== 'granted') {
          console.warn('Local notification permission not granted:', req?.display || perm?.display);
          return;
        }
      }

      // Replace any prior copy of this alert so we don't stack duplicates.
      try {
        await LocalNotifications.cancel({ notifications: [{ id: notifId }] });
      } catch (_) {
        /* ignore */
      }

      const platform = Capacitor.getPlatform();
      const notification = {
        id: notifId,
        title,
        body: body || '',
        // Far-future schedule keeps a delivered alert in NC until cleared;
        // Capacitor still presents immediately for near-term `at`.
        schedule: { at: new Date(Date.now() + 250) },
        extra: orderId ? { orderId, kind: kind || '' } : undefined,
      };

      if (platform === 'android') {
        notification.channelId = channelId || ORDER_UPDATES_CHANNEL;
      } else if (sound) {
        notification.sound = NEW_ORDER_SOUND_IOS;
      }

      await LocalNotifications.schedule({
        notifications: [notification],
      });
      return;
    }

    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'granted') {
        // eslint-disable-next-line no-new
        new Notification(title, {
          body,
          silent: !sound,
          tag: orderId && kind ? `skipdq-${kind}-${orderId}` : undefined,
          requireInteraction: true,
        });
      } else if (Notification.permission === 'denied') {
        console.warn('Web Notification permission denied');
      }
    }
  } catch (error) {
    console.warn('notify failed:', error);
  }
}

/** Cancel local alerts for an order (all vendor urgency kinds). */
export async function cancelOrderLocalNotifications(orderId) {
  if (!orderId || !isNative()) return;
  try {
    const LocalNotifications = await getLocalNotifications();
    const notifications = ORDER_NOTIF_KINDS.map((kind) => ({
      id: notificationIdFor(orderId, kind),
    }));
    await LocalNotifications.cancel({ notifications });
  } catch (error) {
    console.warn('cancelOrderLocalNotifications failed:', error);
  }
}

/** Vendor-facing new-order alert (channel + sound + in-app tone). */
export async function notifyNewOrder({ title, body, orderId } = {}) {
  return notify({
    title: title || 'New SkipdQ order',
    body,
    sound: true,
    channelId: NEW_ORDERS_CHANNEL,
    playInAppSound: true,
    orderId,
    kind: NOTIF_KIND_NEW,
  });
}

/** One-shot SLA nudge — system NC + in-app tone (iOS often mutes NC sound while foregrounded). */
export async function notifyVendorSla({ title, body, orderId, kind } = {}) {
  return notify({
    title: title || 'Order needs attention',
    body,
    sound: true,
    channelId: NEW_ORDERS_CHANNEL,
    playInAppSound: true,
    orderId,
    kind: kind || NOTIF_KIND_UNAPPROVED,
  });
}
